import React from 'react';
import moment from 'moment';
import { connect } from 'react-redux';
import _ from 'lodash';
import externalService from '../service/external';
import config from '../config';
import PlayerPluginConfigs from '../util/playerPluginConfigs';
import getCroppingQueryParams from '../util/getCroppingQueryParams';
import getArticleLink from '../util/getArticleLink';
import { appContext } from '../util/decorators';
import Loader from './Loader';
import Promise from 'bluebird';
import dataLayer, { getCustomDimensions, VIDEO_MESSAGE } from 'service/dataLayer';

const FORBIDDEN_ERROR_CODE = 403;

const mapStateToProps = state => ({
  country: state.country,
  language: state.language,
  routeParams: state.routeParams,
  videoSuggestions: _.get(state, 'dataSources.videoSuggestions', []),
});

@connect(mapStateToProps)
@appContext
export default class VideoPlayer extends React.PureComponent {
  static propTypes = {
    video: React.PropTypes.object.isRequired,
    article: React.PropTypes.shape({
      id: React.PropTypes.string.isRequired,
      title: React.PropTypes.string,
    }).isRequired,
    autoplay: React.PropTypes.bool,
    doNotTrackAutoplay: React.PropTypes.bool,
    initPlayer: React.PropTypes.bool,
    videoElementId: React.PropTypes.string.isRequired,
    csid: React.PropTypes.string,
    country: React.PropTypes.string.isRequired,
    videoSuggestions: React.PropTypes.array,
    language: React.PropTypes.string,
    sovSeed: React.PropTypes.string,
    embed: React.PropTypes.bool,
    inline: React.PropTypes.bool,
    routeParams: React.PropTypes.object,
    firstArticle: React.PropTypes.bool,
  };

  constructor() {
    super();
    this.avodPlayerInitialized = false;
    this.state = { loadingSpinnerRemoved: true };
  }

  componentDidMount() {
    /* eslint react/no-did-mount-set-state: 0 */
    this.loadPlayerScript();
    // Showing it here to avoid server -> client dom diff
    this.setState({ loadingSpinnerRemoved: false });
  }

  componentWillReceiveProps({ article, initPlayer, csid }) {
    if (!this.scriptLoaded || !csid) return;

    // running embedPlayer will always re-embed and thus re-start the
    // current video — only do this if the current article actually changed
    if (article.id !== this.props.article.id) {
      this.loadPlayerWrapper(csid);
    }

    if (
      !this.avodPlayerInitialized &&
      this.props.videoSuggestions.length &&
      (this.props.firstArticle || initPlayer)
    ) {
      this.avodPlayerInitialized = true;
      this.loadPlayerWrapper(csid);
    }
  }

  componentWillUnmount() {
    this.disposePlayer();
  }

  disposePlayer() {
    if (window.videoPlayers && window.videoPlayers[this.props.videoElementId]) {
      window.videoPlayers[this.props.videoElementId].dispose();
      delete window.videoPlayers[this.props.videoElementId];
    }
  }

  ensurePlayer() {
    const WAIT_TIMEOUT = 100;
    const MAX_WAIT = 10 * 1000; // 10 seconds
    return new Promise((resolve, reject) => {
      let totalWait = 0;
      const wait = () => {
        if (totalWait >= MAX_WAIT) {
          reject();
        } else if (!window.avodPlayer) {
          setTimeout(wait, WAIT_TIMEOUT);
          totalWait += WAIT_TIMEOUT;
          return;
        }
        resolve();
      };
      wait();
    });
  }

  async loadPlayerScript() {
    try {
      if (!config.IS_SERVER) {
        await this.ensurePlayer();
      }

      this.scriptLoaded = true;

      const {
        firstArticle,
        csid,
        videoSuggestions,
        embed,
        inline,
      } = this.props;

      if (
        (!firstArticle || !csid || !videoSuggestions.length) &&
        !embed &&
        !inline
      ) {
        return;
      }

      this.avodPlayerInitialized = true;
      this.loadPlayerWrapper(csid);
    } catch (error) {
      console.error('Player can not be loaded');
    }
  }

  async loadPlayerWrapper(csid) {
    const mediaGuid = _.get(this.props, 'video.mediaGuid');
    try {
      const cuePointsData = await externalService.getMidrollCuepoints(
        mediaGuid,
      );
      const cuePoints = _.map(cuePointsData, cuePoint => cuePoint.startTime);
      this.loadPlayer(csid, cuePoints);
    } catch (error) {
      this.loadPlayer(csid, []);
    }
  }

  loadPlayer(csid, cuePoints) {
    const videoElementId = this.props.videoElementId;

    const browserNotSupported = _.invoke(
      window,
      'avodPlayer.requiresProgressiveDownload',
    );
    this.setState({ browserNotSupported });

    if (
      (window.videoPlayers && window.videoPlayers[videoElementId]) ||
      browserNotSupported
    ) {
      return;
    }

    const {
      article,
      video,
      country,
      embed,
      sovSeed,
      routeParams,
      inline,
      autoplay,
      doNotTrackAutoplay,
    } = this.props;

    const poster = video.posterUrl
      ? `${video.posterUrl}${getCroppingQueryParams(4)}`
      : '';

    const customDimensions = getCustomDimensions({
      article,
      video,
      country,
      embed,
      cuePoints,
    });

    const playerPluginConfig = new PlayerPluginConfigs({
      article,
      csid,
      video,
      country,
      sovSeed,
      embed,
      routeParams,
      cuePoints,
      inline,
      customDimensions,
    });

    const t = this.context.app.t;

    const currentArticle = _.find(this.props.videoSuggestions, {
      id: article.id,
    });
    const videoSuggestions = currentArticle
      ? _.without(this.props.videoSuggestions, currentArticle)
      : _.initial(this.props.videoSuggestions);

    playerPluginConfig.suggestions = {
      countdownTime: embed ? 10 : -1,
      cuepoint: 0,
      labels: {
        watchAgain: t('VideoPlayer.watch_again'),
        title: t('VideoPlayer.related_videos'),
      },
      suggestions: _.map(videoSuggestions, suggestion => {
        const embedLink = routeParams.partnerId
          ? `/embed/${routeParams.partnerId}/${suggestion.id}?autoplay`
          : `/embed/${suggestion.id}?autoplay`;
        return {
          title: suggestion.title,
          date: moment(suggestion.settings.publishTimestamp)
            .locale(this.props.language)
            .format('Do MMMM'),
          img: suggestion.videos[0].data.posterUrl + getCroppingQueryParams(1),
          link: embed
            ? embedLink
            : getArticleLink(suggestion, { href: true, excludeHost: false }),
        };
      }),
    };

    // modify the DOM manually, so that React won't complain when Video.js does its DOM manipulation
    this.refs.videoPlayer.innerHTML = `<div id="${videoElementId}" class="avodp"></div>`;

    if (typeof window.avodPlayer === 'undefined') return;

    if (!window.videoPlayers) window.videoPlayers = {};

    const errorMessage = {
      error: {
        code: 0,
        message: 'We are unable to play this video. This is either because you are abroad or because of a technical error.',
      },
    };

    function handleFetchError(err, done) {
      if (_.get(err, 'response.status') === FORBIDDEN_ERROR_CODE) {
        // Log Forbidden
        dataLayer.trackVideoError(VIDEO_MESSAGE.OUTSIDE_REGION, customDimensions);
      } else {
        // Log general error
        dataLayer.trackVideoError(VIDEO_MESSAGE.NOT_FOUND, customDimensions);
      }
      done(errorMessage);
    }

    function asyncSourceLoader(done) {
      externalService
        .getStreamLinks(country, video.mediaGuid)
        .then(stream => {
          done(
            _.get(stream, 'embedded.prioritizedStreams[0].links.stream.href'),
          );
        })
        .catch(err => handleFetchError(err, done));
    }

    // On mobile devices the player needs to block the thread while fetching the video.
    function syncSourceLoader(done) {
      try {
        const stream = externalService.getStreamLinksSync(
          country,
          video.mediaGuid,
        );
        done(_.get(stream, 'embedded.prioritizedStreams[0].links.stream.href'));
      } catch (err) {
        handleFetchError(err, done);
      }
    }

    // Deduce if autplay should be toggled on mobile
    function hasMobileAutoplay() {
      return (
        !embed &&
        _.has(article, ['tags', 'freeTexts']) &&
        _.includes(article.tags.freeTexts, 'MAS')
      );
    }

    // Angle - the player should start source from specified time (if provided)
    const isAngle = _.has(article, ['tags', 'videoTypes']) && _.includes(article.tags.videoTypes, 'angle');

    // Facebook in-app browser is triggering false adBlockerDetection, try to get hold of the UA string
    const ua = navigator.userAgent || navigator.vendor || window.opera || '';

    const notTwitter = ua.indexOf('Twitter') < 0;

    const player = (window.videoPlayers[
      videoElementId
    ] = window.avodPlayer(videoElementId, {
      adBlockerDetection: {
        // https://www.whatismybrowser.com/developers/tools/user-agent-parser/browse
        enabled: !(ua.indexOf('FBAN') > -1 || ua.indexOf('FBAV') > -1), // Turn off if FB in-app
        baitUrl: '/ad.banner.adview.html?adzone=top&adsize=300x250',
      },
      autoplay: notTwitter && autoplay,
      mobileAutoplay: notTwitter && autoplay && hasMobileAutoplay(),
      // Player will only autoplay if focused. Embeds in iFrame will never focus. Therefore forceAutoplay.
      forceAutoplay: notTwitter && autoplay && embed,
      doNotTrackAutoplay: doNotTrackAutoplay,
      language: this.props.language,
      plugins: playerPluginConfig,
      sourceLoader: {
        asynchronousCallback: asyncSourceLoader,
        synchronousCallback: syncSourceLoader,
      },
      theme: {
        name: 'viasport',
        themeColor: '#0080c7',
      },
      poster: poster,
      timeBox: {
        startTimestamp: video.beginPlaybackFrom,
        endTimestamp: video.endPlaybackAt,
        getCurrentTime: () => this.context.app.serverTime().valueOf(),
      },
      startTime: isAngle && video.beginPlaybackFrom || null,
      // Current default behaviour of player is to skip prerolls if startTime != 0
      forcePreroll: isAngle && video.beginPlaybackFrom,
    }));

    // Logging for if the freewheel asset id is invalid, which causes no ads to show
    player.on('adRequestSucceeded', (event, response) => {
      const error = _.find(_.get(response, 'response.errors.errors'), {
        name: 'INVALID_ASSET_ID',
      });
      if (error && window.Raven) {
        window.Raven.captureMessage(
          `An invalid freewheel asset id was found: ${error.context}`,
          { level: 'error' },
        );
      }
    });

    // pause all other videos when pressing play
    player.on('play', () => {
      for (const i in window.videoPlayers) {
        if (i !== videoElementId) {
          window.videoPlayers[i].pause();
        }
      }
    });

    // Using mixpanel tracking because of google analytics taking a few hours to show data
    /* eslint-disable no-undef */
    const track = () =>
      mixpanel &&
      mixpanel.track(embed ? 'Video start embed' : 'Video start', {
        slug: article.slug,
        country,
      });
    /* eslint-enable no-undef */

    // The player has logic for autoplay, not always depending on the prop
    if (player._player.autoplay()) {
      track();
    } else {
      player.one('controlStart', track);
    }

    this.setState({ loadingSpinnerRemoved: true });
  }

  render() {
    const { t } = this.context.app;
    const { browserNotSupported, loadingSpinnerRemoved } = this.state;

    return (
      <div className="video-wrapper">

        {!loadingSpinnerRemoved &&
          <div className="loader-container"><Loader /></div>}

        {browserNotSupported &&
          <div className="video-player-message">
            {t('VideoPlayer.unsupported_browser')}
            {' '}
            <a
              target="_blank"
              href={`https://www.google.${this.props.country}/chrome/browser/desktop/`}>
              {t('VideoPlayer.chrome_link_text')}
            </a>
          </div>}

        <div ref="videoPlayer" />
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/component/VideoPlayer.jsx